import html2pdf from 'html2pdf.js/dist/html2pdf.min.js';
import { generateFeesReport } from './generateFeesReport';
import { uploadSummaryPdfToS3, vinForUpload } from './summaryUpload';

export const feesPdfFileName = (response) =>
  response?.["TransactionID"] ? `${response["TransactionID"]}.pdf` : 'feesOutput.pdf';

export const feesPdfOptions = (response) => ({
  filename: feesPdfFileName(response),
  image: { type: 'jpeg', quality: 0.98 }, // <-- controls embedded image quality
  jsPDF: {
    unit: 'in',
    format: 'letter', // equivalent to [8.5, 11], just clearer
    orientation: 'portrait',
  },
  html2canvas: {
    scale: 2, // <-- default can render blurry text. 2 sharpens it
    useCORS: true, // if you load any external images
  },
  pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }, // <-- if content spans multiple pages
});

const hasResponse = (response) => response && Object.keys(response).length > 0;

// Save the fees summary PDF to the user's machine.
export const downloadFeesSummaryPdf = (feesFormData, response) => {
  if (!hasResponse(response)) {
    return;
  }
  const content = generateFeesReport(feesFormData, response);
  return html2pdf().set(feesPdfOptions(response)).from(content).save();
};

// Transaction IDs already uploaded (or uploading) in this browser session.
// The output page can mount more than once for the same quote (React
// StrictMode, Back then forward), and each transaction only needs to be in S3
// once.
const uploadedTransactionIds = new Set();

// Upload the fees summary PDF to S3 so it can be redownloaded later from the
// Past Transactions page. Runs as soon as an output page shows a quote, not
// when the user clicks Download, so every quote gets archived. Failures are
// logged only - the user still has the quote on screen.
export const uploadFeesSummaryToS3 = async (feesFormData, response) => {
  const transactionId = response?.["TransactionID"];
  if (!hasResponse(response) || !transactionId || uploadedTransactionIds.has(transactionId)) {
    return;
  }
  uploadedTransactionIds.add(transactionId);

  try {
    const content = generateFeesReport(feesFormData, response);
    const pdfBlob = await html2pdf().set(feesPdfOptions(response)).from(content).output('blob');

    const result = await uploadSummaryPdfToS3({
      pdfBlob,
      fileName: feesPdfFileName(response),
      // Refi quote IDs end in _REF, purchase ones in _F.
      callType: transactionId.endsWith('_REF') ? 'Refi-Fees' : 'Fees',
      vin: vinForUpload(feesFormData?.vinCode, transactionId),
    });
    console.log('Fees summary uploaded to S3:', result?.key);
  } catch (error) {
    // Let a later visit to the output page try again.
    uploadedTransactionIds.delete(transactionId);
    console.error('S3 Upload Failed:', error);
  }
};
